// Wires the singleton Socket.io client into the Zustand store.
//
// Key invariants:
// 1. The handler set is registered exactly once per mount; we clean up with
//    .off(...) and flush the partial throttle timer on unmount.
// 2. `agent:partial` carries one assistant text-delta per event (backend
//    contract). The frontend accumulates per-agent buffers and flushes them
//    at most every PARTIAL_FLUSH_MS into `partialTextByAgent`. The backend
//    emits partials at 20+/s; without this throttle React would re-render
//    every token.
// 3. `agent:message` of type `assistant_text` (or `tool_use`) clears the
//    accumulator for that agent — the streamed tokens have been replaced by
//    a real persisted message. This logic lives in the store's
//    `appendMessage`; we only have to dispatch.
// 4. `connectionStatus` is the single source of truth for the green/yellow/
//    red indicator; we mirror Socket.io's lifecycle into it.

import { useEffect } from 'react';
import type { Socket } from 'socket.io-client';
import { getSocket } from '../lib/socket';
import { useAppStore } from '../stores/app.store';
import { useToastStore } from '../stores/toast.store';
import { useNotificationsStore, type FeedNotification } from '../stores/notifications.store';
import { playNotificationChime } from '../lib/notification-sound';
import { abrirUrlEnLaApp } from '../lib/abrir-en-la-app';
import { t } from '../i18n';
import type {
  AgentCreatedEvent,
  AgentDeletedEvent,
  AgentMessageEvent,
  AgentPartialEvent,
  AgentUpdatedEvent,
  ConcurrencyUpdatedEvent,
  LiveActivity,
  Message,
  RateLimitState,
} from '../types';

// Sprint 17 — wire shape of the `agent:activity` socket event. `idle`
// is conveyed as a separate kind on the wire (a discriminated union)
// because Socket.io serialises `null` payloads inconsistently across
// transports. The store reduces it to `null` on receipt.
type AgentActivityWireEvent =
  | LiveActivity
  | { agentId: string; kind: 'idle' };

// Sprint 20.3 PR-5 — wire shape of the `agent:preparing` socket
// event. Mirrors `SessionEventMap['agent:preparing']` on the backend
// (one literal per phase). The store collapses 'ready' to a delete
// of the per-agent entry; 'cloning' / 'failed' set the entry.
type AgentPreparingWireEvent =
  | { agentId: string; runnerId: string; phase: 'cloning' }
  | { agentId: string; runnerId: string; phase: 'ready' }
  | {
      agentId: string;
      runnerId: string;
      phase: 'failed';
      reason:
        | 'auth_failed'
        | 'origin_unreachable'
        | 'corrupted_existing'
        | 'disk_full'
        | 'timeout'
        | 'runner_offline';
      message: string;
    };

const PARTIAL_FLUSH_MS = 80;

export interface UseSocketOptions {
  socket?: Socket; // injected for tests
  onAgentMessage?: (e: AgentMessageEvent) => void;
  onAgentPartial?: (agentId: string, accumulated: string) => void;
  /** Oficina v2: solo el flujo de agentes y mensajes. Sin campanita, sin avisos persistentes de la
   *  flota (con acciones que en /oficina no hacen nada) y sin el aviso de reinicio. */
  sinAvisos?: boolean;
}

/**
 * 04-11 — el toast de cada aviso Tier A, por el id de su fila. Una fila que el servidor actualiza (mismo id: el hilo de
 * Santi mientras siga sin leer) sustituye a su toast en vez de apilar otro, como la notificación del aparato (tag).
 */
const toastDeAviso = new Map<string, string>();
const MAX_TOASTS_RECORDADOS = 100;

/** El toast persistente de un aviso Tier A: «Ver» abre su agente o, si no tiene (los de Santi), su dirección. */
export function toastDeAvisoTierA(n: FeedNotification): void {
  const toasts = useToastStore.getState();
  const anterior = toastDeAviso.get(n.id);
  if (anterior) toasts.dismiss(anterior);
  const { agentId, url } = n;
  const id = toasts.push({
    message: url && n.body ? t('notifications.conTexto', { titulo: n.title, texto: n.body }) : n.title,
    durationMs: 0, // persistent — dismissed by the founder, not a timer
    action: agentId
      ? { label: t('notifications.openAction'), onClick: () => useAppStore.getState().selectAgent(agentId) }
      : url
        ? { label: t('notifications.openAction'), onClick: () => abrirUrlEnLaApp(url) }
        : undefined,
  });
  toastDeAviso.delete(n.id);
  toastDeAviso.set(n.id, id);
  if (toastDeAviso.size > MAX_TOASTS_RECORDADOS) toastDeAviso.delete(toastDeAviso.keys().next().value!);
}

export function useSocket(opts: UseSocketOptions = {}): void {
  const setConnectionStatus = useAppStore((s) => s.setConnectionStatus);
  const upsertAgent = useAppStore((s) => s.upsertAgent);
  const removeAgent = useAppStore((s) => s.removeAgent);
  const setConcurrency = useAppStore((s) => s.setConcurrency);
  const appendMessage = useAppStore((s) => s.appendMessage);
  const setPartialText = useAppStore((s) => s.setPartialText);
  const setLiveActivity = useAppStore((s) => s.setLiveActivity);
  const clearAllLiveActivity = useAppStore((s) => s.clearAllLiveActivity);
  const setRateLimitState = useAppStore((s) => s.setRateLimitState);
  const startPreparing = useAppStore((s) => s.startPreparing);
  const failPreparing = useAppStore((s) => s.failPreparing);
  const clearPreparing = useAppStore((s) => s.clearPreparing);
  // Sprint 20.3 PR-7 (D013-EC-22) — truncation latch setter.
  const setStreamTruncated = useAppStore((s) => s.setStreamTruncated);
  // Sprint 20.5 PR-2 (D013-EC-68) — SESSION_LOST latch setter.
  const setSessionLost = useAppStore((s) => s.setSessionLost);
  // Sprint 20.6 PR-2 (D013-EC-72) — SESSION_EXPIRED latch setter.
  const setSessionExpired = useAppStore((s) => s.setSessionExpired);

  useEffect(() => {
    const socket = opts.socket ?? getSocket();

    const onConnect = () => setConnectionStatus('connected');
    const onDisconnect = () => {
      setConnectionStatus('disconnected');
      // Sprint 17 — drop every "Working · 4:32" timer the moment the
      // socket goes down. Keeping the indicator alive after a disconnect
      // would lie to the user (we have no idea whether the agent is
      // still working). Reconnect handshake replays current state.
      clearAllLiveActivity();
    };
    const onReconnectAttempt = () => setConnectionStatus('reconnecting');
    const onReconnect = () => setConnectionStatus('connected');

    const onAgentCreated = (e: AgentCreatedEvent) => upsertAgent(e.agent);
    // Sprint 13 — when a turn-end update lands for the conversation the user
    // is currently viewing AND the tab is foregrounded, clear the badge
    // automatically. Otherwise the user has to click the already-open
    // conversation to dismiss it. Read via getState() (no subscription) so
    // selection / visibility changes don't re-bind the singleton listener.
    const onAgentUpdated = (e: AgentUpdatedEvent) => {
      upsertAgent(e.agent);
      if (
        e.agent.unread &&
        typeof document !== 'undefined' &&
        document.visibilityState === 'visible' &&
        useAppStore.getState().selectedAgentId === e.agent.id
      ) {
        useAppStore.getState().markRead(e.agent.id);
      }
    };
    const onAgentDeleted = (e: AgentDeletedEvent) => removeAgent(e.agentId);
    const onConcurrencyUpdated = (e: ConcurrencyUpdatedEvent) =>
      setConcurrency(e.concurrency);

    // Sprint 5.6 (F6) — partial-ghost fix. When a real assistant_text or
    // tool_use message lands, the live-typing buffer is no longer correct
    // (the persisted message is the source of truth). The store also
    // clears `partialTextByAgent[agentId]`, but we must clear the
    // closure-local `buffers` and `dirty` set here too — otherwise a
    // pending flushTimer could re-emit stale text *after* the real
    // message arrived, ghosting half a turn back into the UI.
    const onAgentMessage = (e: AgentMessageEvent) => {
      appendMessage(e.agentId, e.message);
      if (e.message.type === 'assistant_text' || e.message.type === 'tool_use') {
        buffers.delete(e.agentId);
        dirty.delete(e.agentId);
      }
      opts.onAgentMessage?.(e);
    };

    // Per-agent token-buffer + single shared flush timer. Using a single
    // timer (vs one-per-agent) keeps the bookkeeping simple — when it fires
    // we flush every dirty agent at once.
    const buffers = new Map<string, string>();
    const dirty = new Set<string>();
    let flushTimer: ReturnType<typeof setTimeout> | null = null;

    const flush = () => {
      flushTimer = null;
      for (const agentId of dirty) {
        const acc = buffers.get(agentId) ?? '';
        setPartialText(agentId, acc);
        opts.onAgentPartial?.(agentId, acc);
      }
      dirty.clear();
    };

    const onAgentPartial = (e: AgentPartialEvent) => {
      const prev = buffers.get(e.agentId) ?? '';
      buffers.set(e.agentId, prev + e.text);
      dirty.add(e.agentId);
      if (flushTimer === null) {
        flushTimer = setTimeout(flush, PARTIAL_FLUSH_MS);
      }
    };

    // Initial state — Socket.io fires `connect` only on the first connection
    // event, so seed the store with the current state.
    if (socket.connected) setConnectionStatus('connected');
    else setConnectionStatus('disconnected');

    socket.on('connect', onConnect);
    socket.on('disconnect', onDisconnect);
    socket.io.on('reconnect_attempt', onReconnectAttempt);
    socket.io.on('reconnect', onReconnect);

    socket.on('agent:created', onAgentCreated);
    socket.on('agent:updated', onAgentUpdated);
    socket.on('agent:deleted', onAgentDeleted);
    socket.on('agent:message', onAgentMessage);
    // Estado de entrega de los mensajes del usuario (pendiente → enviado → recibido).
    const onMessageDelivery = (e: { agentId: string; messageId: string; delivery: NonNullable<Message['delivery']> }) => {
      useAppStore.getState().setMessageDelivery(e.agentId, e.messageId, e.delivery);
    };
    socket.on('agent:message_delivery', onMessageDelivery);
    socket.on('agent:partial', onAgentPartial);
    socket.on('concurrency:updated', onConcurrencyUpdated);

    // Sprint rate-limit-banner-ssot — banner state driven by the
    // backend broadcaster. Reflects real Anthropic usage; auto-clears.
    const onRateLimitState = (e: RateLimitState) => setRateLimitState(e);
    socket.on('rate_limit_state', onRateLimitState);

    // Sprint 17 — single-source live indicator. Backend emits exactly
    // one event per state transition (open thinking, open tool, idle).
    // The reduction `kind === 'idle' ? null : event` keeps the store
    // shape simple: presence in the map = live, absence = idle.
    const onAgentActivity = (e: AgentActivityWireEvent) => {
      if (e.kind === 'idle') {
        setLiveActivity(e.agentId, null);
      } else {
        setLiveActivity(e.agentId, e);
      }
    };
    socket.on('agent:activity', onAgentActivity);

    // Sprint 20.3 PR-5 — auto-clone preparing signal. The toast in
    // `SpawnPreparingToast.tsx` reads `preparingByAgent` from the
    // store; we collapse the wire phase here.
    const onAgentPreparing = (e: AgentPreparingWireEvent) => {
      if (e.phase === 'cloning') {
        startPreparing(e.agentId, e.runnerId);
      } else if (e.phase === 'ready') {
        clearPreparing(e.agentId);
      } else {
        failPreparing(e.agentId, e.runnerId, e.reason, e.message);
      }
    };
    socket.on('agent:preparing', onAgentPreparing);

    // Sprint 20.3 PR-7 (D013-EC-22) — single-shot truncation marker
    // per agent-turn. Latches the founder-mode pill until the
    // persisted assistant_text message arrives (cleared in the store
    // via `appendMessage`).
    const onAgentTextTruncated = (e: { agentId: string }) => {
      setStreamTruncated(e.agentId);
    };
    socket.on('agent:text_truncated', onAgentTextTruncated);

    // Sprint 20.5 PR-2 (D013-EC-68) — runner-emitted SESSION_LOST. The
    // runner could not respawn the Claude child for a queued
    // user_message; SessionLostBanner renders above the composer with
    // a Retry CTA. The agent's status has already been flipped to
    // `error` by the SessionManager, so `agent:updated` arrives in the
    // same tick and the composer will be in its error rendering — the
    // banner is the recovery affordance on top of that.
    const onAgentSessionLost = (e: {
      agentId: string;
      code: 'SESSION_LOST';
      recoverable: boolean;
      message: string | null;
    }) => {
      setSessionLost(e.agentId, e.recoverable, e.message);
    };
    socket.on('agent:session_lost', onAgentSessionLost);

    // Sprint 20.6 PR-2 (D013-EC-72) — runner respawned this agent on
    // boot but Anthropic dropped the resumed sessionId. Render the
    // SessionExpiredBanner with a "Start a new agent" CTA.
    const onAgentSessionExpired = (e: {
      agentId: string;
      code: 'SESSION_EXPIRED';
      recoverable: boolean;
      message: string | null;
    }) => {
      setSessionExpired(e.agentId, e.recoverable, e.message);
    };
    socket.on('agent:session_expired', onAgentSessionExpired);

    // Sprint 20.6 PR-2 (D013-EC-72) — runner respawned the CLI on boot
    // after a daemon reload. Surface a 2 s founder-mode toast so the
    // user understands the parpadeo they just saw was intentional.
    const onAgentRespawned = (_e: {
      agentId: string;
      runnerId: string;
      reason: 'daemon_restart';
    }) => {
      useToastStore.getState().push({
        message: t('respawnedToast.message'),
        durationMs: 2_000,
      });
    };
    if (!opts.sinAvisos) socket.on('agent:respawned', onAgentRespawned);

    // Sprint notification-center (spec D1) — live feed events. Tier A
    // (requires action) additionally raises a persistent toast + the
    // chime; Tier B/C only update the feed + bell badge here (Tier C's
    // urgency travels out-of-band via Telegram + push).
    const onNotificationNew = (e: { notification: FeedNotification; sound: boolean }) => {
      useNotificationsStore.getState().receive(e.notification);
      if (e.notification.tier === 'A') {
        if (e.sound) playNotificationChime();
        toastDeAvisoTierA(e.notification);
      }
    };
    if (!opts.sinAvisos) socket.on('notification:new', onNotificationNew);

    // 09-10: el OPEN_AGENT del service worker (tocar una notificación) ya no se escucha aquí — antes
    // seleccionaba el agente en /home (el panel viejo). Lo atiende toda la app en
    // features/oficina/abrirDesdeAviso.ts, que abre su chat en la oficina.

    return () => {
      socket.off('notification:new', onNotificationNew);
      socket.off('connect', onConnect);
      socket.off('disconnect', onDisconnect);
      socket.io.off('reconnect_attempt', onReconnectAttempt);
      socket.io.off('reconnect', onReconnect);
      socket.off('agent:created', onAgentCreated);
      socket.off('agent:updated', onAgentUpdated);
      socket.off('agent:deleted', onAgentDeleted);
      socket.off('agent:message', onAgentMessage);
      socket.off('agent:message_delivery', onMessageDelivery);
      socket.off('agent:partial', onAgentPartial);
      socket.off('concurrency:updated', onConcurrencyUpdated);
      socket.off('rate_limit_state', onRateLimitState);
      socket.off('agent:activity', onAgentActivity);
      socket.off('agent:preparing', onAgentPreparing);
      socket.off('agent:text_truncated', onAgentTextTruncated);
      socket.off('agent:session_lost', onAgentSessionLost);
      socket.off('agent:session_expired', onAgentSessionExpired);
      socket.off('agent:respawned', onAgentRespawned);
      if (flushTimer !== null) {
        clearTimeout(flushTimer);
        flushTimer = null;
      }
      buffers.clear();
      dirty.clear();
    };
    // The handler closures depend on the stable store setters; we intentionally
    // pin the effect to mount so the singleton socket isn't re-bound on every
    // render. Including opts.* in deps would defeat the singleton's purpose.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
}

// Emits `agent:input` over the singleton WebSocket. Returns a promise that
// resolves on the server's ack (`{ ok: true }`) or rejects with the error
// string the server returned. Falls back gracefully on socket errors so the
// chat input can show a useful message.
// Sprint 8 — `chipIds` (optional) lists previously-uploaded attachment chips
// the backend should claim and attach to the user message. Empty/undefined
// preserves the legacy text-only payload exactly.
export function sendAgentInput(
  agentId: string,
  text: string,
  chipIds?: string[],
  // 30-09 — cuánto esperar la confirmación. Un agente recién contratado en un runner confirma cuando su orden de
  // arranque ha salido (tras preparar la copia del proyecto, hasta 90 s): quien escribe justo al contratar lo sube.
  esperaMs = 10_000,
  // 01-11 (pelotas) — `preguntaId`: la tarjeta «Para Santi» que estaba en pantalla cuando Pau escribió. Un «sí»
  // tecleado solo vale para ESA tarjeta: el servidor comprueba que sigue siendo la que espera y, si no, no la envía.
  // Sin tarjeta no se manda nada (el payload queda como siempre).
  extra?: { preguntaId?: number },
): Promise<void> {
  return new Promise((resolve, reject) => {
    let socket: Socket;
    try {
      socket = getSocket();
    } catch (err) {
      reject(err instanceof Error ? err : new Error('socket unavailable'));
      return;
    }
    const payload: { agentId: string; text: string; chipIds?: string[]; preguntaId?: number } = {
      agentId,
      text,
    };
    if (chipIds && chipIds.length > 0) payload.chipIds = chipIds;
    if (extra?.preguntaId !== undefined) payload.preguntaId = extra.preguntaId;
    socket
      .timeout(esperaMs)
      .emit(
        'agent:input',
        payload,
        (
          timeoutErr: Error | null,
          resp: { ok: boolean; error?: string; code?: string } | undefined,
        ) => {
          if (timeoutErr) {
            reject(timeoutErr);
            return;
          }
          if (!resp || !resp.ok) {
            // Sprint 20.3 PR-6 — preserve the backend error code on the
            // rejected Error so callers can branch on RUNNER_OFFLINE
            // (failover banner) without parsing the message string.
            const errInst = new Error(resp?.error ?? 'Send failed') as Error & {
              code?: string;
            };
            if (resp?.code) errInst.code = resp.code;
            reject(errInst);
            return;
          }
          resolve();
        },
      );
  });
}
